import { useEffect, useState } from "react";
import { ITournamentCategoryDetail } from "../../models/ITournament";
import { ChevronDown, ChevronUp, Edit, Plus, Save, Trash, X } from "@mynaui/icons-react";
import { EditTournamentCategoryDTO } from "../../types/tournament";
import { editCategory } from "../../api/tournaments";
import {
  apiErrorMessage,
  getRankName,
  isAmericanoFormat,
  isLiveRoundFormat,
  playersPerSideError,
  playersPerSideFor,
  unitsPerSideFor,
} from "../../utils/methods";
import CategoryScheduleField from "../CategoryScheduleField";
import moment from "moment";
import { feeWindowStatus, FeeWindowStatus } from "../../utils/dates";
import { currencyOptions } from "../../constants/currencies";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  Field,
  FormGrid,
  IconButton,
  Input,
  Segmented,
  Select,
  Tag,
  TagTone,
} from "../../ui";
import {
  BOTH_TEAM_MIN_SIZE_ERROR,
  BOTH_TEAM_MIX_ERROR,
  BOTH_TEAM_SIZE_ERROR,
  bothGenderError,
  bothGenderSummary,
  bothSettingsError,
  fixedTeamSize,
  hasCategoryGenderLimit,
  modeFlags,
  NO_CATEGORY_GENDER_LIMIT,
  RegistrationMode,
  registrationModeOf,
  storedTeamMinSize,
  teamMinSizeOptions,
} from "../../utils/registrationMode";
import "./styles.scss";

const FEE_STATUS_TONE: Record<FeeWindowStatus["kind"], TagTone> = {
  active: "ok",
  upcoming: "info",
  ended: "danger",
  always: "neutral",
};

const REGISTRATION_MODES: { value: RegistrationMode; label: string }[] = [
  { value: "teams", label: "Teams" },
  { value: "individuals", label: "Individuals" },
  { value: "both", label: "Both" },
];

const rankClass = (rank: number) => (rank === 1 ? "gold" : rank === 2 ? "silver" : rank === 3 ? "bronze" : "");

type EditCategoryDialogProps = {
  category: ITournamentCategoryDetail;
  isOpen: boolean;
  mode: "details" | "fees" | "prizes" | "format" | "team";
  onClose: (udpate?: boolean) => void;
  tournament?: { startDate: string; endDate: string };
};

const EditCategoryDialog = (props: EditCategoryDialogProps) => {
  const [category, setCategory] = useState<EditTournamentCategoryDTO>();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [selectedGender, setSelectedGender] = useState<"male" | "female" | "mixed">("male");
  // Mixed Both: the admin opted in to whole-category caps (all teams and solo players).
  const [categoryCapsTicked, setCategoryCapsTicked] = useState(false);

  // Teams / Individuals / Both (spec 2026-10-29): Both is an individual category that also takes complete teams.
  const registrationMode = registrationModeOf(category?.allowIndividualRegistration, category?.allowTeamRegistration);
  const isBoth = registrationMode === "both";
  // The fees drawer doesn't change the mode: its fees follow the saved one.
  const savedAsBoth =
    registrationModeOf(props.category?.allowIndividualRegistration, props.category?.allowTeamRegistration) === "both";
  const formatTeamSize = fixedTeamSize(
    props.category?.format,
    props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.unitSize
  );
  const bothError =
    props.mode === "team" && isBoth
      ? bothSettingsError(formatTeamSize, {
          teamSize: category?.teamSize,
          teamMinSize: category?.teamMinSize,
          teamMaxMen: selectedGender === "mixed" ? category?.teamMaxMen : null,
          teamMaxWomen: selectedGender === "mixed" ? category?.teamMaxWomen : null,
          // This drawer doesn't save fees, so the stored ones decide; a per-team fee must be changed first.
          fees: props.category?.fees ?? [],
        })
      : null;
  // Size, minimum and mix errors sit under their own fields; the rest under the Registration choice.
  const registrationError =
    bothError === BOTH_TEAM_SIZE_ERROR || bothError === BOTH_TEAM_MIN_SIZE_ERROR || bothError === BOTH_TEAM_MIX_ERROR
      ? null
      : bothError;
  // Min. players per team: 2 up to the team size; no minimum = the size (teams must be complete).
  const bothTeamSize = formatTeamSize ?? category?.teamSize ?? null;
  const teamMinSizeValue = category?.teamMinSize ?? bothTeamSize;
  const teamMixLimits = Array.from(
    { length: Math.max(formatTeamSize ?? category?.teamSize ?? 0, 0) + 1 },
    (_, n) => n
  );
  // A Both category must let someone in: 0 men and 0 women is refused (under the gender choice). Older
  // categories saved that way open here as "male" until a gender is picked.
  const genderError =
    props.mode === "team" && isBoth ? bothGenderError(category?.maxMen, category?.maxWomen) : null;
  // Mixed Both: whole-category caps are an opt-in; a cap below the Mixed default (an existing category's
  // included) keeps them on.
  const categoryCapsOn = categoryCapsTicked || hasCategoryGenderLimit(category?.maxMen, category?.maxWomen);
  const toggleCategoryCaps = (on: boolean) => {
    setCategoryCapsTicked(on);
    // Off = the Mixed default: no limit across the category.
    if (!on)
      setCategory((prev) => ({
        ...prev,
        maxMen: NO_CATEGORY_GENDER_LIMIT,
        maxWomen: NO_CATEGORY_GENDER_LIMIT,
      }));
  };

  const inputChangeHandler = (field: string, value: string | number | boolean) => {
    setCategory((prev) => ({ ...prev, [field]: value }));
  };

  const inputFormatChangeHandler = (field: string, value: string | boolean) => {
    setCategory((prev) => ({ ...prev, format: { ...(prev?.format as any), [field]: value } }));
  };

  // The kit Dialog plays the exit animation once the parent turns isOpen off
  const handleClose = (update?: boolean) => {
    if (!update) setInitialCategory();
    props.onClose(update ?? undefined);
    setErrorMessage("");
  };

  const isFakeId = (id: any) => typeof id === "string" && id.length === 13 && /^\d+$/.test(id);

  const handleConfirm = async () => {
    // Roulette: players per side must be a whole number of units, 1-12 (spec 2026-10-14 §4.1).
    const rouletteSideError =
      props.mode === "format" && category?.format?.type === "Roulette"
        ? playersPerSideError(Number(category.format.playersPerSide), Number(category.format.unitSize) || 1)
        : null;
    if (rouletteSideError) {
      setErrorMessage(rouletteSideError);
      return;
    }

    setLoading(true);
    let requestBody: any = {
      name: category?.name,
    };

    switch (props.mode) {
      case "details":
        requestBody = {
          ...requestBody,
          startDate: category?.startDate === "" ? null : category?.startDate,
          endDate: category?.endDate === "" ? null : category?.endDate,
          isIncludedInRanking: category?.isIncludedInRanking ?? false,
        };
        break;
      case "format":
        requestBody = {
          ...requestBody,
          format: category?.format?.type,
          formatBlocks:
            category?.format?.type === "League" || category?.format?.type === "Knockout"
              ? [
                {
                  blockType: category.format.type,
                  orderIndex: 1,
                  leagueRules:
                    category.format.type === "League"
                      ? {
                        encounters: category.format?.encounters,
                        pointsForWin: category.format?.winPts,
                        pointsForDraw: 0,
                        pointsForLoss: category.format?.lostPts,
                        criteria: category.format?.rankingCriteria,
                      }
                      : undefined,
                  knockoutRules:
                    category.format.type === "Knockout"
                      ? {
                        mode: category.format?.mode,
                        hasThirdPlaceMatch: category.format?.thirdPlace,
                      }
                      : undefined,
                  isRankingBlock: true,
                },
              ]
              : isAmericanoFormat(category?.format?.type)
                ? [
                  {
                    blockType: category!.format!.type,
                    orderIndex: 1,
                    isRankingBlock: true,
                    americanoRules: {
                      pointsPerMatch: Number(category!.format!.pointsPerMatch) || 24,
                      // Live formats (Mexicano, Roulette): planned rounds belong to the setup step and
                      // Finish, so never re-send this page's possibly stale copy; the server keeps the
                      // stored value when it's missing (a new Roulette block starts open-ended).
                      ...(!isLiveRoundFormat(category!.format!.type) && {
                        roundsCount: Number(category!.format!.roundsCount) || 4,
                      }),
                      unitSize: Number(category!.format!.unitSize) || 1,
                      // Only Americano pairs by gender.
                      mixedPairing:
                        category!.format!.type === "Americano" && (Number(category!.format!.unitSize) || 1) === 1
                          ? !!category!.format!.mixedPairing
                          : false,
                      ...(category!.format!.type === "Roulette" && {
                        unitsPerSide: unitsPerSideFor(
                          Number(category!.format!.playersPerSide),
                          Number(category!.format!.unitSize) || 1
                        ),
                      }),
                    },
                  },
                ]
              : category?.format?.type === "League-Knockout" || category?.format?.type === "Groups-Knockout"
                ? [
                  {
                    blockType: category.format.type === "League-Knockout" ? "League" : "Group",
                    orderIndex: 1,
                    leagueRules: {
                      encounters: category.format?.encounters,
                      pointsForWin: category.format?.winPts,
                      pointsForDraw: 0,
                      pointsForLoss: category.format?.lostPts,
                      criteria: category.format?.rankingCriteria,
                    },
                    isRankingBlock: false,
                  },
                  {
                    blockType: "Knockout",
                    orderIndex: 2,
                    knockoutRules: {
                      mode: category.format?.mode,
                      hasThirdPlaceMatch: category.format?.thirdPlace,
                    },
                    // The knockout decides the final placings.
                    isRankingBlock: true,
                  },
                ]
                : category?.format?.type === "Custom"
                  ? category.customFormatBlocks?.map((item, id) => ({
                    ...item,
                    orderIndex: id + 1,
                    ...(item.isRankingBlock !== undefined && { IsRankingBlock: item.isRankingBlock }),
                  }))
                  : [],
        };
        break;
      case "team":
        const maxMenValue = Number(category?.maxMen) || 0;
        const maxWomenValue = Number(category?.maxWomen) || 0;
        const maxPlayersValue =
          category?.allowIndividualRegistration
            ? maxMenValue + maxWomenValue > 10
              ? 100
              : maxMenValue + maxWomenValue
            : Number(category?.maxPlayers);
        requestBody = {
          ...requestBody,
          maxPlayers: maxPlayersValue,
          minMen: Number(category?.minMen),
          maxMen: maxMenValue,
          minWomen: Number(category?.minWomen),
          maxWomen: maxWomenValue,
          spotsNum: Number(category?.spotsNum),
          femaleBonusPoints: category?.femaleBonusPoints ? Number(category?.femaleBonusPoints) : 0,
          femaleBonusCap: category?.femaleBonusCap ? Number(category?.femaleBonusCap) : 0,
          allowIndividualRegistration: category?.allowIndividualRegistration ?? false,
          allowTeamRegistration: isBoth,
          // A format with fixed units sets the team size itself; it's stored too, so a later change to a
          // format without units still has one.
          teamSize: isBoth ? formatTeamSize ?? category?.teamSize ?? null : null,
          // null = teams have exactly the team size
          teamMinSize: isBoth ? storedTeamMinSize(category?.teamMinSize, bothTeamSize) : null,
          teamMaxMen: isBoth && selectedGender === "mixed" ? category?.teamMaxMen ?? null : null,
          teamMaxWomen: isBoth && selectedGender === "mixed" ? category?.teamMaxWomen ?? null : null,
        };
        break;
      case "fees":
        requestBody = {
          ...requestBody,
          fees: category?.fees
            ? category?.fees.map((fee) => ({
              id: isFakeId(fee.id) ? null : fee.id,
              amount: Number(fee.amount),
              isEnabled: true,
              currency: fee.currency,
              // A category that takes teams and individuals only has per-player fees.
              perTeam: savedAsBoth ? false : fee.perTeam,
              description: fee.description,
              startDate: fee.startDate || null,
              endDate: fee.endDate || null,
            }))
            : [],
        };
        break;
      case "prizes":
        requestBody = {
          ...requestBody,
          awards: category?.awards
            ? category.awards.map((award) => ({
              id: isFakeId(award.id) ? null : award.id,
              name: award.name,
              rankingPoints: award.rankingPoints,
              includedInVoting: award.includedInVoting,
              clashPoints: award.clashPoints ?? 0,
            }))
            : [],
          prizes: category?.prizes
            ? category?.prizes.map((prize) => ({
              id: isFakeId(prize.id) ? null : prize.id,
              rank: prize.rank,
              amount: Number(prize.amount),
              currency: prize.currency,
              gift: prize.gift,
              rankingPoints: prize.rankingPoints,
              clashPoints: prize.clashPoints ?? 0,
            }))
            : [],
        };
        break;
    }

    const saveFailed = "Couldn't save the changes. Please try again.";
    try {
      const res: any = await editCategory(props.category.id, requestBody);
      // The category endpoints can answer 200 with success: false; that's a refusal, not a save.
      if (res?.success === false) {
        setErrorMessage(apiErrorMessage(res, saveFailed));
        return;
      }
      handleClose(true);
    } catch (error: any) {
      console.error("error editing category", error?.message);
      setErrorMessage(apiErrorMessage(error, saveFailed));
    } finally {
      setLoading(false);
    }
  };

  const getModeTitle = () => {
    switch (props.mode) {
      case "details":
        return "Edit details";
      case "format":
        return "Edit Format";
      case "fees":
        return "Edit Fees";
      case "prizes":
        return "Edit Prizes & Awards";
      case "team":
        return "Edit Team & Players Settings";
    }
  };

  const getModeSubtitle = () => {
    switch (props.mode) {
      case "details":
        return "Name, start date & end date";
      case "format":
        return "Tournament format & rules";
      case "fees":
        return "Tournament fees";
      case "prizes":
        return "Tournament prizes & awards";
      case "team":
        return "Team requirements";
    }
  };

  const onAddCustomBlock = () => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      updatedBlocks.push({
        id: Date.now().toString(),
        blockType: "",
        aliasName: "",
        orderIndex: updatedBlocks.length + 1,
        collapsed: false,
        isRankingBlock: false,
        leagueRules: {
          criteria: "total",
          encounters: 1,
          pointsForDraw: 0,
          pointsForLoss: 0,
          pointsForWin: 3,
        },
        knockoutRules: {
          hasThirdPlaceMatch: true,
          mode: "single",
        },
      });

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const handleDeleteCustomBlock = (id: string) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];
      return { ...prev, customFormatBlocks: updatedBlocks.filter((x) => x.id !== id) };
    });
  };

  const customBlockInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        [field]: value,
      };

      if (field == "blockType") {
        updatedBlocks[idx] = {
          ...updatedBlocks[idx],
          leagueRules:
            value === "League" || value === "Group"
              ? { criteria: "total", encounters: 1, pointsForDraw: 0, pointsForLoss: 0, pointsForWin: 3 }
              : undefined,
          knockoutRules: value === "Knockout" ? { hasThirdPlaceMatch: true, mode: "single" } : undefined,
        };
      }

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const customBlockLeagueInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        leagueRules: {
          ...updatedBlocks[idx].leagueRules,
          [field]: value,
        },
      };

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const customBlockKnockoutInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        knockoutRules: {
          ...updatedBlocks[idx].knockoutRules,
          [field]: value,
        },
      };

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const prizeInputChangeHandler = (id: string, field: string, value: string) => {
    setCategory((prev) => {
      let updatedPrizes = [...prev?.prizes!];

      const idx = updatedPrizes.findIndex((x) => x.id === id);
      updatedPrizes[idx] = {
        ...updatedPrizes[idx],
        amount: field === "prizeOption" ? undefined : updatedPrizes[idx].amount,
        gift: field === "prizeOption" ? "" : updatedPrizes[idx].gift,
        [field]:
          field === "rankingPoints" || field === "clashPoints" ? parseInt(value) || 0 : value,
      };

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const addPrizeHandler = () => {
    setCategory((prev) => {
      let updatedPrizes = [...prev?.prizes!];

      const newRank = updatedPrizes.length === 0 ? 1 : Math.max(...updatedPrizes.map((prize) => prize.rank)) + 1;

      updatedPrizes.push({
        id: Date.now().toString(),
        rank: newRank,
        amount: undefined,
        currency: "EGP",
        prizeOption: "cash",
        rankingPoints: 0,
        clashPoints: 0,
        gift: "",
        added: false,
        edit: false,
      });

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const cancelPrizeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedPrizes = [...prev?.prizes!];
      const idx = updatedPrizes.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        updatedPrizes[idx] = {
          ...updatedPrizes[idx],
          edit: false,
        };
      }

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const savePrizeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedPrizes = [...prev?.prizes!];
      const idx = updatedPrizes.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        if (updatedPrizes[idx].edit) {
          updatedPrizes[idx] = {
            ...updatedPrizes[idx],
            edit: false,
            amount: Number(updatedPrizes[idx].amount),
          };
        } else if (!updatedPrizes[idx].added) {
          updatedPrizes[idx] = {
            ...updatedPrizes[idx],
            added: true,
            amount: Number(updatedPrizes[idx].amount),
          };
        } else {
          updatedPrizes[idx] = {
            ...updatedPrizes[idx],
            edit: true,
            prizeOption: updatedPrizes[idx].gift ? "other" : "cash",
          };
        }
      }

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const deletePrizeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedPrizes = [...prev?.prizes!];
      return { ...prev, prizes: updatedPrizes.filter((prize) => prize.id !== id) };
    });
  };

  const awardInputChangeHandler = (id: string, field: string, value: string | number | boolean) => {
    setCategory((prev) => {
      let updatedAwards = [...prev?.awards!];

      const idx = updatedAwards.findIndex((x) => x.id === id);
      updatedAwards[idx] = {
        ...updatedAwards[idx],
        [field]: value,
      };

      return { ...prev, awards: updatedAwards };
    });
  };

  const cancelAwardHandler = (id: string) => {
    setCategory((prev) => {
      let updatedAwards = [...prev?.awards!];
      const idx = updatedAwards.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        updatedAwards[idx] = {
          ...updatedAwards[idx],
          edit: false,
        };
      }

      return { ...prev, awards: updatedAwards };
    });
  };

  const saveAwardHandler = (id: string) => {
    setCategory((prev) => {
      let updatedAwards = [...prev?.awards!];
      const idx = updatedAwards.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        if (updatedAwards[idx].edit) {
          updatedAwards[idx] = {
            ...updatedAwards[idx],
            edit: false,
          };
        } else if (!updatedAwards[idx].added) {
          updatedAwards[idx] = {
            ...updatedAwards[idx],
            added: true,
          };
        } else {
          updatedAwards[idx] = {
            ...updatedAwards[idx],
            edit: true,
          };
        }
      }

      return { ...prev, awards: updatedAwards };
    });
  };

  const deleteAwardHandler = (id: string) => {
    setCategory((prev) => {
      let updatedAwards = [...prev?.awards!];
      return { ...prev, awards: updatedAwards.filter((prize) => prize.id !== id) };
    });
  };

  const addAwardHandler = () => {
    setCategory((prev) => {
      let updatedAwards = [...prev?.awards!];

      updatedAwards.push({
        id: Date.now().toString(),
        name: "",
        playerId: "",
        rankingPoints: 1,
        clashPoints: 0,
        includedInVoting: true,
        added: false,
        edit: false,
      });

      return { ...prev, awards: updatedAwards };
    });
  };

  const feeInputChangeHandler = (id: string, field: string, value: string | boolean | number | null) => {
    setCategory((prev) => {
      let updatedFees = [...prev?.fees!];

      const idx = updatedFees.findIndex((x) => x.id === id);
      updatedFees[idx] = {
        ...updatedFees[idx],
        [field]: value,
      };

      return { ...prev, fees: updatedFees };
    });
  };

  const cancelFeeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedFees = [...prev?.fees!];
      const idx = updatedFees.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        updatedFees[idx] = {
          ...updatedFees[idx],
          edit: false,
        };
      }

      return { ...prev, fees: updatedFees };
    });
  };

  const saveFeeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedFees = [...prev?.fees!];
      const idx = updatedFees.findIndex((prize) => prize.id === id);

      if (idx !== -1) {
        if (updatedFees[idx].edit) {
          updatedFees[idx] = {
            ...updatedFees[idx],
            edit: false,
          };
        } else if (!updatedFees[idx].added) {
          updatedFees[idx] = {
            ...updatedFees[idx],
            added: true,
          };
        } else {
          updatedFees[idx] = {
            ...updatedFees[idx],
            edit: true,
          };
        }
      }

      return { ...prev, fees: updatedFees };
    });
  };

  const deleteFeeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedFees = [...prev?.fees!];
      return { ...prev, fees: updatedFees.filter((prize) => prize.id !== id) };
    });
  };

  const addFeeHandler = () => {
    setCategory((prev) => {
      let updatedFees = [...prev?.fees!];

      updatedFees.push({
        id: Date.now().toString(),
        currency: "EGP",
        description: "",
        isEnabled: true,
        perTeam: false,
        amount: undefined,
        added: false,
        edit: false,
        startDate: null,
        endDate: null,
      });

      return { ...prev, fees: updatedFees };
    });
  };

  const setInitialCategory = () => {
    setCategoryCapsTicked(false);
    setCategory({
      customFormatBlocks: props.category?.customFormatBlocks
        ? props.category?.customFormatBlocks
          .sort((a, b) => (a.orderIndex > b.orderIndex ? 1 : -1))
          .map((item) => ({ ...item, collapsed: true }))
        : [],
      endDate: props.category?.endDate ?? "",
      fees: props.category?.fees.map((fee) => ({
        currency: fee.currency,
        description: fee.description,
        perTeam: fee.perTeam,
        amount: fee.amount,
        id: fee.id,
        isEnabled: fee.isEnabled,
        added: true,
        edit: false,
        startDate: fee.startDate ?? null,
        endDate: fee.endDate ?? null,
      })),
      format: {
        type: props.category?.format,
        encounters: props.category?.leagueRules.length === 1 ? props.category?.leagueRules[0].encounters : 0,
        lostPts: props.category?.leagueRules.length === 1 ? props.category?.leagueRules[0].pointsForLoss : 0,
        mode: props.category?.knockoutRules.length === 1 ? props.category?.knockoutRules[0].mode : "single",
        rankingCriteria: props.category?.leagueRules.length === 1 ? props.category?.leagueRules[0].criteria : "total",
        thirdPlace:
          props.category?.knockoutRules.length === 1 ? props.category?.knockoutRules[0].hasThirdPlaceMatch : false,
        winPts: props.category?.leagueRules.length === 1 ? props.category?.leagueRules[0].pointsForWin : 0,
        pointsPerMatch:
          props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.pointsPerMatch ?? 24,
        roundsCount:
          props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.roundsCount ?? 4,
        unitSize: props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.unitSize ?? 1,
        mixedPairing:
          props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.mixedPairing ?? false,
        // Stored as units per side (missing on older servers = 2); the form edits players per side.
        playersPerSide: playersPerSideFor(
          props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.unitsPerSide,
          props.category?.customFormatBlocks?.find((b) => b.americanoRules)?.americanoRules?.unitSize
        ),
      },
      maxMen: props.category?.teamRequirements.maxMen,
      maxPlayers: props.category?.allowIndividualRegistration
        ? (props.category?.teamRequirements.maxMen || 0) + (props.category?.teamRequirements.maxWomen || 0)
        : props.category?.maxPlayers,
      maxWomen: props.category?.teamRequirements.maxWomen,
      minMen: props.category?.teamRequirements.minMen,
      minWomen: props.category?.teamRequirements.minWomen,
      femaleBonusPoints: props.category?.femaleBonusPoints ?? 0,
      femaleBonusCap: props.category?.femaleBonusCap ?? 0,
      isIncludedInRanking: props.category?.isIncludedInRanking ?? false,
      allowIndividualRegistration: props.category?.allowIndividualRegistration ?? false,
      allowTeamRegistration: props.category?.allowTeamRegistration ?? false,
      teamSize: props.category?.teamSize ?? null,
      // Reads give the effective minimum (the size when teams must be complete): kept as "no minimum" so it
      // follows a size change.
      teamMinSize: storedTeamMinSize(props.category?.teamMinSize, formatTeamSize ?? props.category?.teamSize),
      teamMaxMen: props.category?.teamMaxMen ?? null,
      teamMaxWomen: props.category?.teamMaxWomen ?? null,
      name: props.category?.categoryName,
      awards: props.category?.awards.map((award) => ({
        id: award.id,
        name: award.name,
        added: true,
        edit: false,
        playerId: award.playerId || "",
        rankingPoints: award.rankingPoints || 0,
        includedInVoting: award.includedInVoting || false,
        clashPoints: award.clashPoints || 0,
      })),
      prizes: props.category?.prizes.map((prize) => ({
        added: true,
        currency: prize.currency,
        gift: prize.gift,
        rank: prize.rank,
        amount: prize.amount,
        edit: false,
        id: prize.id,
        rankingPoints: prize.rankingPoints || 0,
        clashPoints: prize.clashPoints || 0,
      })),
      spotsNum: props.category?.spotsNum,
      startDate: props.category?.startDate ?? "",
    });
  };

  useEffect(() => {
    if (props.category) {
      const { minMen, minWomen, maxMen, maxWomen } = props.category.teamRequirements;

      // Determine gender type based on team requirements
      if (minMen > 0 && minWomen > 0) {
        // Both genders required
        setSelectedGender("mixed");
      } else if (maxMen === 0 && maxWomen > 0) {
        // Only females allowed
        setSelectedGender("female");
      } else if (maxWomen === 0 && maxMen > 0) {
        // Only males allowed
        setSelectedGender("male");
      } else if (maxMen > 0 && maxWomen > 0) {
        // Both genders allowed (mixed)
        setSelectedGender("mixed");
      } else {
        // Default to male if unclear
        setSelectedGender("male");
      }
    }

    setInitialCategory();
  }, [props.category]);

  useEffect(() => {
    if (!category?.allowIndividualRegistration) return;

    const maxMenValue = Number(category?.maxMen) || 0;
    const maxWomenValue = Number(category?.maxWomen) || 0;
    const sum = maxMenValue + maxWomenValue;
    const nextMaxPlayers = sum > 10 ? 100 : sum;

    if (Number(category?.maxPlayers) !== nextMaxPlayers) {
      setCategory((prev) => (prev ? { ...prev, maxPlayers: nextMaxPlayers } : prev));
    }
  }, [category?.allowIndividualRegistration, category?.maxMen, category?.maxWomen]);

  useEffect(() => {
    setTimeout(() => {
      setErrorMessage("");
    }, 5000);
  }, [errorMessage]);

  // Units are fixed once an Americano-like stage has started (the server refuses the change too).
  const unitsLocked = !!props.category?.customFormatBlocks?.some(
    (b) => isAmericanoFormat(b.blockType) && b.hasStarted
  );

  // Teams / Individuals / Both (the old "Allow Individual Registration" tick, plus Both)
  const changeRegistrationMode = (mode: RegistrationMode) => {
    const flags = modeFlags(mode);
    setCategory((prev) => ({
      ...prev,
      ...flags,
      // Turning individual registration on: set maxPlayers to maxMen + maxWomen
      ...(flags.allowIndividualRegistration &&
        !prev?.allowIndividualRegistration && { maxPlayers: (prev?.maxMen || 0) + (prev?.maxWomen || 0) }),
    }));
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      size={props.mode === "details" ? "sm" : "md"}
      title={getModeTitle()}
      subtitle={getModeSubtitle()}
      className="edit-category-dialog"
      bodyClassName="edit-category-dialog__body"
      footer={
        <>
          {errorMessage ? (
            <Alert tone="danger" className="edit-category-dialog__error">
              {errorMessage}
            </Alert>
          ) : null}
          <Button variant="ghost" onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading} disabled={!!bothError || !!genderError}>
            Confirm
          </Button>
        </>
      }
    >
      {props.mode === "details" ? (
        <div className="details-container">
          <Field label="Name">
            {(fieldId) => (
              <Input id={fieldId} value={category?.name} onChange={(e) => inputChangeHandler("name", e.target.value)} />
            )}
          </Field>
          {props.tournament && (
            <CategoryScheduleField
              tournamentStartDate={props.tournament.startDate}
              tournamentEndDate={props.tournament.endDate}
              value={{ startDate: category?.startDate || null, endDate: category?.endDate || null }}
              onChange={(next) => {
                inputChangeHandler("startDate", next.startDate || "");
                inputChangeHandler("endDate", next.endDate || "");
              }}
            />
          )}
          <Checkbox
            id="isIncludedInRanking"
            checked={category?.isIncludedInRanking ?? false}
            onChange={(checked) => inputChangeHandler("isIncludedInRanking", checked)}
            label="Include in Ranking"
          />
        </div>
      ) : props.mode === "format" ? (
        <div className="format-container">
          <div className="format-options" role="radiogroup" aria-label="Format">
            {[
              { label: "League", value: "League" },
              { label: "Knockout", value: "Knockout" },
              { label: "Americano", value: "Americano" },
              { label: "Mexicano", value: "Mexicano" },
              { label: "Roulette", value: "Roulette" },
              { label: "Custom", value: "Custom" },
            ].map((format, id) => (
              <button
                type="button"
                role="radio"
                aria-checked={category?.format?.type === format.value}
                key={id}
                className={`option-card ${category?.format?.type === format.value ? "selected" : ""}`}
                onClick={() => {
                  if (
                    category?.customFormatBlocks?.every((x) => !x.hasStarted) ||
                    (category?.customFormatBlocks?.length === 1 &&
                      (format.value === "Custom" || format.value === category.customFormatBlocks[0].blockType))
                  )
                    inputFormatChangeHandler("type", format.value);
                  else setErrorMessage("Unable to update! Please delete schedule first");
                }}
              >
                {format.label}
              </button>
            ))}
          </div>

          {(category?.format?.type.includes("League") || category?.format?.type.includes("Groups")) && (
            <section className="rules-section">
              <p className="section-title">{category.format.type.includes("League") ? "League" : "Groups"} Rules</p>
              <FormGrid columns={2}>
                <Field label="Points for Win">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      value={category?.format?.winPts}
                      onChange={(e) => inputFormatChangeHandler("winPts", e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Points for Loss">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      value={category?.format?.lostPts}
                      onChange={(e) => inputFormatChangeHandler("lostPts", e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Encounters">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      value={category?.format?.encounters}
                      onChange={(e) => inputFormatChangeHandler("encounters", e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Ranking Criteria">
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      block
                      onChange={(e) => inputFormatChangeHandler("rankingCriteria", e.target.value)}
                      value={category?.format?.rankingCriteria ?? ""}
                    >
                      <option value={"total"}>Total Points</option>
                      <option value={"direct"}>Direct Matches</option>
                    </Select>
                  )}
                </Field>
              </FormGrid>
            </section>
          )}

          {category?.format?.type.includes("Knockout") && (
            <section className="rules-section">
              <p className="section-title">Knockout Rules</p>
              <FormGrid columns={2}>
                <Field label="Mode">
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      block
                      onChange={(e) => inputFormatChangeHandler("mode", e.target.value)}
                      value={category?.format?.mode ?? ""}
                    >
                      <option value={"single"}>Single elimination</option>
                      <option value={"double"}>Double elimination</option>
                    </Select>
                  )}
                </Field>
                <Field label="3rd Place Match">
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      block
                      onChange={(e) =>
                        inputFormatChangeHandler("thirdPlace", Number(e.target.value) === 1 ? true : false)
                      }
                      value={category?.format?.thirdPlace ? 1 : 0}
                    >
                      <option value={1}>Yes</option>
                      <option value={0}>No</option>
                    </Select>
                  )}
                </Field>
              </FormGrid>
            </section>
          )}

          {isAmericanoFormat(category?.format?.type) && (
            <section className="rules-section">
              <p className="section-title">{category?.format?.type} Rules</p>
              <Field
                label="Points per Match"
                hint="Rounds are configured when creating the schedule (they depend on the number of players and courts)."
              >
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    type="number"
                    min={1}
                    value={category?.format?.pointsPerMatch ?? 24}
                    onChange={(e) => inputFormatChangeHandler("pointsPerMatch", e.target.value)}
                  />
                )}
              </Field>
              <FormGrid columns={2}>
                <Field
                  label="Unit Size"
                  hint={
                    category?.format?.type === "Roulette"
                      ? "1 = individual players, 2+ = fixed teams formed at the venue."
                      : undefined
                  }
                >
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      min={1}
                      disabled={unitsLocked}
                      value={category?.format?.unitSize ?? 1}
                      onChange={(e) => {
                        inputFormatChangeHandler("unitSize", e.target.value);
                        if (Number(e.target.value) !== 1) inputFormatChangeHandler("mixedPairing", false);
                      }}
                    />
                  )}
                </Field>
                {category?.format?.type === "Roulette" && (
                  <Field label="Players per Side" hint="A multiple of the unit size, up to 12.">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={1}
                        max={12}
                        disabled={unitsLocked}
                        value={category?.format?.playersPerSide ?? ""}
                        onChange={(e) => inputFormatChangeHandler("playersPerSide", e.target.value)}
                      />
                    )}
                  </Field>
                )}
                {unitsLocked && (
                  <p className="ui-field__hint">
                    {category?.format?.type === "Roulette"
                      ? "Unit size and players per side can't change once the stage has started."
                      : "Unit size can't change once the stage has started."}
                  </p>
                )}
                {/* Only Americano pairs by gender; Mexicano and Roulette never do (spec 2026-09-29, 2026-10-14). */}
                {category?.format?.type === "Americano" && (
                  <Field label="Mixed Pairing">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        disabled={Number(category?.format?.unitSize ?? 1) !== 1}
                        value={category?.format?.mixedPairing ? 1 : 0}
                        onChange={(e) => inputFormatChangeHandler("mixedPairing", Number(e.target.value) === 1)}
                      >
                        <option value={1}>Yes</option>
                        <option value={0}>No</option>
                      </Select>
                    )}
                  </Field>
                )}
              </FormGrid>
            </section>
          )}

          {category?.format?.type.includes("Custom") && (
            <div className="custom-format-container">
              {category?.customFormatBlocks?.map((block, id) => (
                <div className="custom-block-item" key={id}>
                  <div
                    className="header-container"
                    role="button"
                    tabIndex={0}
                    aria-expanded={!block.collapsed}
                    onClick={() => customBlockInputChangeHandler(block.id, "collapsed", !block.collapsed)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                      e.preventDefault();
                      customBlockInputChangeHandler(block.id, "collapsed", !block.collapsed);
                    }}
                  >
                    <p className="custom-block-title">
                      Block {id + 1}
                      {block.collapsed && block.blockType ? ` - ${block.aliasName || block.blockType}` : ""}
                    </p>
                    <div className="row">
                      <IconButton
                        label="Delete"
                        variant="danger"
                        size="sm"
                        icon={<Trash size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (block.hasStarted) setErrorMessage("Unable to update! Please delete schedule first");
                          else handleDeleteCustomBlock(block.id);
                        }}
                      />
                      {block.collapsed ? <ChevronDown /> : <ChevronUp />}
                    </div>
                  </div>
                  {block.collapsed ? null : (
                    <div className="custom-block-body">
                      <Select
                        block
                        aria-label="Block type"
                        value={block.blockType}
                        onChange={(e) =>
                          block.hasStarted
                            ? setErrorMessage("Unable to update! Please delete schedule first")
                            : customBlockInputChangeHandler(block.id, "blockType", e.target.value)
                        }
                      >
                        <option value="" disabled>
                          -- Select a block type --
                        </option>
                        <option value="League">League</option>
                        <option value="Group">Group</option>
                        <option value="Knockout">Knockout</option>
                      </Select>
                      <Field label="Name">
                        {(fieldId) => (
                          <Input
                            id={fieldId}
                            type="text"
                            placeholder="Enter format name"
                            value={block.aliasName || ""}
                            onChange={(e) => customBlockInputChangeHandler(block.id, "aliasName", e.target.value)}
                          />
                        )}
                      </Field>
                      <Checkbox
                        id={`isRankingBlock-${block.id}`}
                        checked={block.isRankingBlock ?? false}
                        onChange={(checked) => customBlockInputChangeHandler(block.id, "isRankingBlock", checked)}
                        label="Ranking Block"
                      />
                      {block.blockType === "Knockout" ? (
                        <FormGrid columns={2}>
                          <Field label="Mode">
                            {(fieldId) => (
                              <Select
                                id={fieldId}
                                block
                                onChange={(e) =>
                                  block.hasStarted
                                    ? setErrorMessage("Unable to update! Please delete schedule first")
                                    : customBlockKnockoutInputChangeHandler(block.id, "mode", e.target.value)
                                }
                                value={block.knockoutRules?.mode ?? ""}
                              >
                                <option value={"single"}>Single elimination</option>
                                <option value={"double"}>Double elimination</option>
                              </Select>
                            )}
                          </Field>
                          <Field label="3rd Place Match">
                            {(fieldId) => (
                              <Select
                                id={fieldId}
                                block
                                onChange={(e) =>
                                  block.hasStarted
                                    ? setErrorMessage("Unable to update! Please delete schedule first")
                                    : customBlockKnockoutInputChangeHandler(
                                        block.id,
                                        "hasThirdPlaceMatch",
                                        Number(e.target.value) === 1 ? true : false
                                      )
                                }
                                value={block.knockoutRules?.hasThirdPlaceMatch ? 1 : 0}
                              >
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                              </Select>
                            )}
                          </Field>
                        </FormGrid>
                      ) : block.blockType === "League" || block.blockType === "Group" ? (
                        <FormGrid columns={2}>
                          <Field label="Points for Win">
                            {(fieldId) => (
                              <Input
                                id={fieldId}
                                type="number"
                                value={block.leagueRules?.pointsForWin}
                                onChange={(e) =>
                                  customBlockLeagueInputChangeHandler(block.id, "pointsForWin", e.target.value)
                                }
                              />
                            )}
                          </Field>
                          <Field label="Points for Loss">
                            {(fieldId) => (
                              <Input
                                id={fieldId}
                                type="number"
                                value={block.leagueRules?.pointsForLoss}
                                onChange={(e) =>
                                  customBlockLeagueInputChangeHandler(block.id, "pointsForLoss", e.target.value)
                                }
                              />
                            )}
                          </Field>
                          <Field label="Encounters">
                            {(fieldId) => (
                              <Input
                                id={fieldId}
                                type="number"
                                value={block.leagueRules?.encounters}
                                onChange={(e) =>
                                  block.hasStarted
                                    ? setErrorMessage("Unable to update! Please delete schedule first")
                                    : customBlockLeagueInputChangeHandler(block.id, "encounters", e.target.value)
                                }
                              />
                            )}
                          </Field>
                          <Field label="Ranking Criteria">
                            {(fieldId) => (
                              <Select
                                id={fieldId}
                                block
                                onChange={(e) =>
                                  customBlockLeagueInputChangeHandler(block.id, "criteria", e.target.value)
                                }
                                value={block.leagueRules?.criteria ?? ""}
                              >
                                <option value={"total"}>Total Points</option>
                                <option value={"direct"}>Direct Matches</option>
                              </Select>
                            )}
                          </Field>
                        </FormGrid>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
              <Button variant="tint" icon={<Plus size={18} />} onClick={onAddCustomBlock} className="add-btn">
                Add Block
              </Button>
            </div>
          )}
        </div>
      ) : props.mode === "team" ? (
        <div className="team-container">
          <FormGrid columns={2}>
            <Field label={category?.allowIndividualRegistration ? "Num. of Players" : "Num. of Teams"}>
              {(fieldId) => (
                <Input
                  id={fieldId}
                  type="number"
                  value={category?.spotsNum}
                  onChange={(e) => inputChangeHandler("spotsNum", e.target.value)}
                />
              )}
            </Field>
            {!category?.allowIndividualRegistration && (
              <Field label="Max players per team">
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    block
                    value={category?.maxPlayers ?? ""}
                    onChange={(e) => {
                      inputChangeHandler("maxPlayers", e.target.value);
                      if (selectedGender === "male") inputChangeHandler("maxMen", e.target.value);
                      if (selectedGender === "female") inputChangeHandler("maxWomen", e.target.value);
                      if (selectedGender === "mixed") {
                        const intValue = Number(e.target.value);
                        inputChangeHandler("maxWomen", intValue === 100 ? 100 : Math.floor(intValue / 2));
                        inputChangeHandler("maxMen", intValue === 100 ? 100 : Math.floor(intValue / 2));
                      }
                    }}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                    <option value={10}>10</option>
                    <option value={100}>Unlimited</option>
                  </Select>
                )}
              </Field>
            )}
            {isBoth && (
              <Field label="Players per team" error={bothError === BOTH_TEAM_SIZE_ERROR ? bothError : undefined}>
                {(fieldId) =>
                  formatTeamSize !== null ? (
                    <p className="team-size-fixed">Teams of {formatTeamSize} (from the format)</p>
                  ) : (
                    <Input
                      id={fieldId}
                      type="number"
                      min={2}
                      value={category?.teamSize ?? ""}
                      invalid={bothError === BOTH_TEAM_SIZE_ERROR}
                      onChange={(e) => {
                        const teamSize = parseInt(e.target.value);
                        setCategory((prev) => ({ ...prev, teamSize: Number.isNaN(teamSize) ? null : teamSize }));
                      }}
                    />
                  )
                }
              </Field>
            )}
            {isBoth && (
              <Field
                label="Min. players per team"
                error={bothError === BOTH_TEAM_MIN_SIZE_ERROR ? bothError : undefined}
                hint={
                  teamMinSizeValue != null && bothTeamSize != null && teamMinSizeValue < bothTeamSize
                    ? "Fill short teams with waiting players before they play."
                    : undefined
                }
              >
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    block
                    disabled={teamMinSizeOptions(bothTeamSize).length === 0}
                    invalid={bothError === BOTH_TEAM_MIN_SIZE_ERROR}
                    value={teamMinSizeValue ?? ""}
                    onChange={(e) => {
                      const teamMinSize = parseInt(e.target.value);
                      setCategory((prev) => ({ ...prev, teamMinSize: Number.isNaN(teamMinSize) ? null : teamMinSize }));
                    }}
                  >
                    {teamMinSizeOptions(bothTeamSize).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </FormGrid>

          <Field label="Registration" error={registrationError}>
            <Segmented
              ariaLabel="Registration"
              options={REGISTRATION_MODES.map((option) => ({
                ...option,
                // Players play alone in a unit-size-1 format: nothing to pair.
                disabled: option.value === "both" && formatTeamSize === 1,
              }))}
              value={registrationMode}
              onChange={changeRegistrationMode}
            />
          </Field>

          <Field error={genderError}>
            <div className="gender-options" role="radiogroup" aria-label="Gender">
              {["male", "female", "mixed"].map((gender, id) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedGender === gender}
                  className={`option-card ${selectedGender === gender ? "selected" : ""}`}
                  key={id}
                  onClick={() => {
                    setSelectedGender(gender as "male" | "female" | "mixed");
                    // One side 0 makes a single-gender category; the other side must stay open (`||`, not
                    // `??`: a stored 0 would leave 0 men and 0 women).
                    if (gender === "male") {
                      inputChangeHandler("minWomen", 0);
                      inputChangeHandler("maxWomen", 0);
                      if (category?.allowIndividualRegistration) {
                        inputChangeHandler("maxMen", category?.maxMen || 100);
                        inputChangeHandler("maxPlayers", category?.maxMen || 100);
                      } else {
                        inputChangeHandler("maxMen", category?.maxPlayers ?? 100);
                      }
                    }
                    if (gender === "female") {
                      inputChangeHandler("minMen", 0);
                      inputChangeHandler("maxMen", 0);
                      if (category?.allowIndividualRegistration) {
                        inputChangeHandler("maxWomen", category?.maxWomen || 100);
                        inputChangeHandler("maxPlayers", category?.maxWomen || 100);
                      } else {
                        inputChangeHandler("maxWomen", category?.maxPlayers ?? 100);
                      }
                    }
                    if (gender === "mixed") {
                      // Both: the box follows the caps kept here (ticked when either is below 100).
                      setCategoryCapsTicked(false);
                      if (category?.allowIndividualRegistration) {
                        const maxMen = category?.maxMen || 100;
                        const maxWomen = category?.maxWomen || 100;
                        inputChangeHandler("maxMen", maxMen);
                        inputChangeHandler("maxWomen", maxWomen);
                        inputChangeHandler("maxPlayers", maxMen + maxWomen);
                      } else {
                        inputChangeHandler(
                          "maxMen",
                          category?.maxPlayers ? (category.maxPlayers == 100 ? 100 : category.maxPlayers / 2) : 100
                        );
                        inputChangeHandler(
                          "maxWomen",
                          category?.maxPlayers ? (category.maxPlayers == 100 ? 100 : category.maxPlayers / 2) : 100
                        );
                      }
                    }
                  }}
                >
                  {gender}
                </button>
              ))}
            </div>
          </Field>

          {isBoth && selectedGender === "mixed" && (
            <>
              <section className="rules-section">
                <p className="section-title">Each team</p>
                <FormGrid columns={2}>
                  <Field
                    label="Max. men per team"
                    error={bothError === BOTH_TEAM_MIX_ERROR ? bothError : undefined}
                  >
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        invalid={bothError === BOTH_TEAM_MIX_ERROR}
                        value={category?.teamMaxMen ?? ""}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            teamMaxMen: e.target.value === "" ? null : parseInt(e.target.value),
                          }))
                        }
                      >
                        <option value="">No limit</option>
                        {teamMixLimits.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label="Max. women per team">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        invalid={bothError === BOTH_TEAM_MIX_ERROR}
                        value={category?.teamMaxWomen ?? ""}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            teamMaxWomen: e.target.value === "" ? null : parseInt(e.target.value),
                          }))
                        }
                      >
                        <option value="">No limit</option>
                        {teamMixLimits.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </FormGrid>
              </section>

              <section className="rules-section">
                <p className="section-title">Whole category</p>
                <Checkbox
                  label="Also limit men and women across the whole category (all teams and solo players)"
                  checked={categoryCapsOn}
                  onChange={toggleCategoryCaps}
                />
                {categoryCapsOn && (
                  <FormGrid columns={2}>
                    <Field label="Max. men in total">
                      {(fieldId) => (
                        <Input
                          id={fieldId}
                          type="number"
                          min={0}
                          value={category?.maxMen}
                          onChange={(e) => {
                            const maxMen = parseInt(e.target.value) || 0;
                            setCategory((prev) => {
                              const maxWomen = prev?.maxWomen || 0;
                              const sum = maxMen + maxWomen;
                              return { ...prev, maxMen, maxPlayers: sum > 10 ? 100 : sum };
                            });
                          }}
                        />
                      )}
                    </Field>
                    <Field label="Max. women in total">
                      {(fieldId) => (
                        <Input
                          id={fieldId}
                          type="number"
                          min={0}
                          value={category?.maxWomen}
                          onChange={(e) => {
                            const maxWomen = parseInt(e.target.value) || 0;
                            setCategory((prev) => {
                              const maxMen = prev?.maxMen || 0;
                              const sum = maxMen + maxWomen;
                              return { ...prev, maxWomen, maxPlayers: sum > 10 ? 100 : sum };
                            });
                          }}
                        />
                      )}
                    </Field>
                  </FormGrid>
                )}
                <p className="gender-summary">
                  {bothGenderSummary({
                    teamMaxMen: category?.teamMaxMen,
                    teamMaxWomen: category?.teamMaxWomen,
                    limitCategory: categoryCapsOn,
                    maxMen: category?.maxMen,
                    maxWomen: category?.maxWomen,
                  })}
                </p>
              </section>
            </>
          )}

          {category?.allowIndividualRegistration && !isBoth && selectedGender === "mixed" && (
            <section className="rules-section">
              <p className="section-title">Gender Distribution</p>
              <FormGrid columns={2}>
                <Field label="Max. # of Males">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      value={category?.maxMen}
                      onChange={(e) => {
                        const maxMen = parseInt(e.target.value) || 0;
                        setCategory((prev) => {
                          const maxWomen = prev?.maxWomen || 0;
                          const sum = maxMen + maxWomen;
                          return {
                            ...prev,
                            maxMen,
                            maxPlayers: sum > 10 ? 100 : sum,
                          };
                        });
                      }}
                    />
                  )}
                </Field>
                <Field label="Max. # of Females">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      type="number"
                      value={category?.maxWomen}
                      onChange={(e) => {
                        const maxWomen = parseInt(e.target.value) || 0;
                        setCategory((prev) => {
                          const maxMen = prev?.maxMen || 0;
                          const sum = maxMen + maxWomen;
                          return {
                            ...prev,
                            maxWomen,
                            maxPlayers: sum > 10 ? 100 : sum,
                          };
                        });
                      }}
                    />
                  )}
                </Field>
              </FormGrid>
            </section>
          )}

          {!category?.allowIndividualRegistration && (
            <section className="rules-section">
              <p className="section-title">Team Requirements</p>

              {(selectedGender === "male" || selectedGender === "mixed") && (
                <FormGrid columns={2}>
                  <Field label="Min. # of Males per Team">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        value={category?.minMen}
                        onChange={(e) => inputChangeHandler("minMen", e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Max. # of Males per Team">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        value={category?.maxMen ?? ""}
                        onChange={(e) => inputChangeHandler("maxMen", e.target.value)}
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                        <option value={7}>7</option>
                        <option value={8}>8</option>
                        <option value={9}>9</option>
                        <option value={10}>10</option>
                        <option value={100}>Unlimited</option>
                      </Select>
                    )}
                  </Field>
                </FormGrid>
              )}

              {(selectedGender === "female" || selectedGender === "mixed") && (
                <FormGrid columns={2}>
                  <Field label="Min. # of Females per Team">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        value={category?.minWomen}
                        onChange={(e) => inputChangeHandler("minWomen", e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Max. # of Females per Team">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        value={category?.maxWomen ?? ""}
                        onChange={(e) => inputChangeHandler("maxWomen", e.target.value)}
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                        <option value={7}>7</option>
                        <option value={8}>8</option>
                        <option value={9}>9</option>
                        <option value={10}>10</option>
                        <option value={100}>Unlimited</option>
                      </Select>
                    )}
                  </Field>
                </FormGrid>
              )}

              {(selectedGender === "female" || selectedGender === "mixed") && (
                <>
                  <p className="section-title">Bonus Points</p>
                  <FormGrid columns={2}>
                    <Field label="Female Bonus Points">
                      {(fieldId) => (
                        <Input
                          id={fieldId}
                          type="number"
                          value={category?.femaleBonusPoints ?? 0}
                          onChange={(e) => inputChangeHandler("femaleBonusPoints", e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Female Bonus Cap">
                      {(fieldId) => (
                        <Input
                          id={fieldId}
                          type="number"
                          value={category?.femaleBonusCap ?? 0}
                          onChange={(e) => inputChangeHandler("femaleBonusCap", e.target.value)}
                        />
                      )}
                    </Field>
                  </FormGrid>
                </>
              )}
            </section>
          )}
        </div>
      ) : props.mode === "prizes" ? (
        <div className="prizes-award-container">
          <section className="rules-section">
            <p className="section-title">Prizes</p>
            <div className="entry-list">
              {category?.prizes?.map((prize, id) => {
                console.log("Prize data:", prize);
                const editing = prize.edit || !prize.added;
                return (
                  <div className={`entry-item prize-item ${editing ? "edit" : ""}`} key={id}>
                    <div className="entry-head">
                      <div className="left-section">
                        {prize.added && !prize.edit && (
                          <span className={`rank-badge ${rankClass(prize.rank)}`}>{prize.rank}</span>
                        )}
                        <div className="entry-main">
                          <p className="entry-title">{getRankName(prize.rank)} Place</p>
                          {editing ? null : (
                            <div className="prize-display">
                              <p className="entry-detail">
                                {prize.gift ? "Other" : "Cash Prize"} -{" "}
                                {prize.gift ? prize.gift : `${prize.amount?.toLocaleString()} ${prize.currency}`}
                                {prize.rankingPoints ? " (" + prize.rankingPoints + " Ranking Points)" : ""}
                              </p>
                              {prize.clashPoints ? (
                                <p className="entry-meta">Clash Points: {prize.clashPoints}</p>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="action-buttons">
                        {!prize.added ? (
                          <Button size="sm" icon={<Plus size={16} />} onClick={() => savePrizeHandler(prize.id)}>
                            Add
                          </Button>
                        ) : prize.edit ? (
                          <Button size="sm" icon={<Save size={16} />} onClick={() => savePrizeHandler(prize.id)}>
                            Save
                          </Button>
                        ) : (
                          <IconButton label="Edit" icon={<Edit size={18} />} onClick={() => savePrizeHandler(prize.id)} />
                        )}
                        {editing ? (
                          <IconButton
                            label={prize.added ? "Cancel" : "Delete"}
                            icon={<X size={18} />}
                            onClick={() => (prize.added ? cancelPrizeHandler(prize.id) : deletePrizeHandler(prize.id))}
                          />
                        ) : (
                          <IconButton
                            label="Delete"
                            variant="danger"
                            icon={<Trash size={18} />}
                            onClick={() => deletePrizeHandler(prize.id)}
                          />
                        )}
                      </div>
                    </div>
                    {editing && (
                      <div className="entry-inputs">
                        <Select
                          block
                          aria-label="Prize type"
                          value={prize.prizeOption ?? ""}
                          onChange={(e) => {
                            prizeInputChangeHandler(prize.id, "prizeOption", e.target.value);
                          }}
                        >
                          <option value="cash">Cash</option>
                          <option value="other">Other</option>
                        </Select>
                        {prize.prizeOption === "cash" && (
                          <Select
                            block
                            aria-label="Currency"
                            value={prize.currency}
                            onChange={(e) => prizeInputChangeHandler(prize.id, "currency", e.target.value)}
                          >
                            {currencyOptions(prize.currency).map((code) => (
                              <option key={code} value={code}>
                                {code}
                              </option>
                            ))}
                          </Select>
                        )}
                        <Input
                          type={prize.prizeOption === "cash" ? "number" : "text"}
                          placeholder={prize.prizeOption === "cash" ? "Amount" : "Gift"}
                          aria-label={prize.prizeOption === "cash" ? "Amount" : "Gift"}
                          value={prize.prizeOption === "cash" ? prize.amount : prize.gift}
                          onChange={(e) =>
                            prizeInputChangeHandler(
                              prize.id,
                              prize.prizeOption === "cash" ? "amount" : "gift",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          type="number"
                          placeholder="Ranking Points"
                          aria-label="Ranking Points"
                          value={prize.rankingPoints || ""}
                          onChange={(e) => prizeInputChangeHandler(prize.id, "rankingPoints", e.target.value)}
                        />
                        <Input
                          type="number"
                          placeholder="Clash Points"
                          aria-label="Clash Points"
                          value={prize.clashPoints || ""}
                          onChange={(e) => prizeInputChangeHandler(prize.id, "clashPoints", e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <Button variant="tint" icon={<Plus size={18} />} onClick={addPrizeHandler} className="add-btn">
                Add Prize
              </Button>
            </div>
          </section>

          <section className="rules-section">
            <p className="section-title">Awards</p>
            <div className="entry-list">
              {category?.awards?.map((award, id) => {
                const editing = award.edit || !award.added;
                return (
                  <div className={`entry-item award-item ${editing ? "edit" : ""}`} key={id}>
                    <div className="entry-head">
                      {editing ? (
                        <div className="award-inputs">
                          <Input
                            type="text"
                            placeholder="Ex: MVP"
                            aria-label="Award name"
                            value={award.name}
                            onChange={(e) => awardInputChangeHandler(award.id, "name", e.target.value)}
                          />
                          <Input
                            type="number"
                            placeholder="Points"
                            aria-label="Points"
                            value={award.rankingPoints || ""}
                            onChange={(e) =>
                              awardInputChangeHandler(award.id, "rankingPoints", parseInt(e.target.value) || 0)
                            }
                          />
                          <Input
                            type="number"
                            placeholder="Clash Pts"
                            aria-label="Clash Pts"
                            value={award.clashPoints || ""}
                            onChange={(e) =>
                              awardInputChangeHandler(award.id, "clashPoints", parseInt(e.target.value) || 0)
                            }
                          />
                        </div>
                      ) : (
                        <div className="entry-main">
                          <p className="entry-title">{award.name}</p>
                          {award.rankingPoints && <p className="entry-meta">Ranking Points: {award.rankingPoints}</p>}
                          {award.clashPoints ? <p className="entry-meta">Clash Points: {award.clashPoints}</p> : null}
                        </div>
                      )}
                      <div className="action-buttons">
                        {!award.added ? (
                          <Button size="sm" icon={<Plus size={16} />} onClick={() => saveAwardHandler(award.id)}>
                            Add
                          </Button>
                        ) : award.edit ? (
                          <Button size="sm" icon={<Save size={16} />} onClick={() => saveAwardHandler(award.id)}>
                            Save
                          </Button>
                        ) : (
                          <IconButton label="Edit" icon={<Edit size={18} />} onClick={() => saveAwardHandler(award.id)} />
                        )}
                        {editing ? (
                          <IconButton
                            label={award.added ? "Cancel" : "Delete"}
                            icon={<X size={18} />}
                            onClick={() => (award.added ? cancelAwardHandler(award.id) : deleteAwardHandler(award.id))}
                          />
                        ) : (
                          <IconButton
                            label="Delete"
                            variant="danger"
                            icon={<Trash size={18} />}
                            onClick={() => deleteAwardHandler(award.id)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <Button variant="tint" icon={<Plus size={18} />} onClick={addAwardHandler} className="add-btn">
                Add Award
              </Button>
            </div>
          </section>
        </div>
      ) : props.mode === "fees" ? (
        <div className="fees-container entry-list">
          {savedAsBoth && <Alert tone="info">Fees are per player when a category takes teams and individuals.</Alert>}
          {category?.fees?.map((fee, id) => {
            const editing = fee.edit || !fee.added;
            return (
              <div className={`entry-item fee-item ${editing ? "edit" : ""}`} key={id}>
                <div className="entry-head">
                  {editing ? (
                    <Input
                      className="fee-description"
                      value={fee.description}
                      placeholder="Description"
                      aria-label="Description"
                      onChange={(e) => feeInputChangeHandler(fee.id, "description", e.target.value)}
                    />
                  ) : (
                    <div className="entry-main">
                      <p className="entry-title">{fee.description}</p>
                      <p className="entry-detail">
                        {fee.perTeam ? "Per Team" : "Per Player"} - {fee.amount} {fee.currency}
                      </p>
                      {(() => {
                        const s = feeWindowStatus({ startDate: fee.startDate, endDate: fee.endDate });
                        return <Tag tone={FEE_STATUS_TONE[s.kind]}>{s.label}</Tag>;
                      })()}
                    </div>
                  )}
                  <div className="action-buttons">
                    {!fee.added ? (
                      <Button size="sm" icon={<Plus size={16} />} onClick={() => saveFeeHandler(fee.id)}>
                        Add
                      </Button>
                    ) : fee.edit ? (
                      <Button size="sm" icon={<Save size={16} />} onClick={() => saveFeeHandler(fee.id)}>
                        Save
                      </Button>
                    ) : (
                      <IconButton label="Edit" icon={<Edit size={18} />} onClick={() => saveFeeHandler(fee.id)} />
                    )}
                    {editing ? (
                      <IconButton
                        label={fee.added ? "Cancel" : "Delete"}
                        icon={<X size={18} />}
                        onClick={() => (fee.added ? cancelFeeHandler(fee.id) : deleteFeeHandler(fee.id))}
                      />
                    ) : (
                      <IconButton
                        label="Delete"
                        variant="danger"
                        icon={<Trash size={18} />}
                        onClick={() => deleteFeeHandler(fee.id)}
                      />
                    )}
                  </div>
                </div>
                {editing && (
                  <>
                    <div className="entry-inputs">
                      <Input
                        type="number"
                        value={fee.amount}
                        placeholder="Amount"
                        aria-label="Amount"
                        onChange={(e) => feeInputChangeHandler(fee.id, "amount", e.target.value)}
                      />
                      <Select
                        block
                        aria-label="Currency"
                        value={fee.currency}
                        onChange={(e) => feeInputChangeHandler(fee.id, "currency", e.target.value)}
                      >
                        {currencyOptions(fee.currency).map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </Select>
                      <Select
                        block
                        aria-label="Fee applies to"
                        disabled={savedAsBoth}
                        value={fee.perTeam && !savedAsBoth ? 1 : 0}
                        onChange={(e) =>
                          feeInputChangeHandler(fee.id, "perTeam", e.target.value === "1" ? true : false)
                        }
                      >
                        <option value={0}>Per player</option>
                        <option value={1}>Per team</option>
                      </Select>
                    </div>
                    <FormGrid columns={2} className="fee-window-row">
                      <Field label="Available from">
                        {(fieldId) => (
                          <DatePicker
                            id={fieldId}
                            selected={fee.startDate ? new Date(fee.startDate) : null}
                            onChange={(d) =>
                              feeInputChangeHandler(
                                fee.id,
                                "startDate",
                                d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : ""
                              )
                            }
                            mode="datetime"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy HH:mm"
                            placeholderText="Always available"
                            isClearable
                          />
                        )}
                      </Field>
                      <Field label="Available until">
                        {(fieldId) => (
                          <DatePicker
                            id={fieldId}
                            selected={fee.endDate ? new Date(fee.endDate) : null}
                            onChange={(d) =>
                              feeInputChangeHandler(
                                fee.id,
                                "endDate",
                                d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : ""
                              )
                            }
                            mode="datetime"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy HH:mm"
                            placeholderText="No end"
                            minDate={fee.startDate ? new Date(fee.startDate) : undefined}
                            isClearable
                          />
                        )}
                      </Field>
                    </FormGrid>
                  </>
                )}
              </div>
            );
          })}
          <Button variant="tint" icon={<Plus size={18} />} onClick={addFeeHandler} className="add-btn">
            Add Fee
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
};

export default EditCategoryDialog;
